import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, ActivityIndicator, SafeAreaView } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { bankAccountService, BankAccountSetup, BankAccount } from '../services/bankAccountService';
import { AuthManager } from '../utils/authManager';

interface BankAccountSetupScreenProps {
  onBack: () => void;
  onComplete?: (bankAccount: BankAccount) => void;
}

const BankAccountSetupScreen: React.FC<BankAccountSetupScreenProps> = ({
  onBack,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [merchantId, setMerchantId] = useState<string>('');
  
  const [formData, setFormData] = useState<BankAccountSetup>({
    accountHolderName: '',
    accountType: 'checking',
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: '',
    bankName: '',
    bankAddress: '',
    verificationMethod: 'micro_deposits'
  });

  const [verificationData, setVerificationData] = useState({
    microDepositAmount1: '',
    microDepositAmount2: ''
  });

  const steps = [
    'Account Information',
    'Bank Verification',
    'Payout Settings',
    'Complete Setup'
  ];

  useEffect(() => {
    loadMerchantId();
  }, []);

  const loadMerchantId = async () => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (businessOwner) {
        setMerchantId(businessOwner.id);
      }
    } catch (error) {
      console.error('Error loading merchant ID:', error);
    }
  };

  const handleInputChange = (field: keyof BankAccountSetup, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = (): boolean => {
    if (!formData.accountHolderName.trim()) {
      Alert.alert('Error', 'Please enter account holder name');
      return false;
    }
    if (!formData.routingNumber || !/^\d{9}$/.test(formData.routingNumber)) {
      Alert.alert('Error', 'Please enter a valid 9-digit routing number');
      return false;
    }
    if (!formData.accountNumber || formData.accountNumber.length < 4) {
      Alert.alert('Error', 'Please enter a valid account number');
      return false;
    }
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      Alert.alert('Error', 'Account numbers do not match');
      return false;
    }
    if (!formData.bankName.trim()) {
      Alert.alert('Error', 'Please enter bank name');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
      setCurrentStep(2);
    } else if (currentStep === 2) {
      await handleVerification();
    } else if (currentStep === 3) {
      setCurrentStep(4);
    }
  };

  const handleVerification = async () => {
    if (!merchantId) {
      Alert.alert('Error', 'Merchant ID not found');
      return;
    }

    setIsLoading(true);
    try {
      // Create bank account
      const bankAccount = await bankAccountService.createBankAccount(merchantId, formData);
      
      if (bankAccount) {
        if (formData.verificationMethod === 'instant_verification') {
          // Simulate instant verification
          const isVerified = await bankAccountService.verifyBankAccount(bankAccount.id, {});
          if (isVerified) {
            setCurrentStep(3);
          } else {
            Alert.alert('Verification Failed', 'Instant verification failed. Please try micro deposits.');
            setFormData(prev => ({ ...prev, verificationMethod: 'micro_deposits' }));
          }
        } else {
          // Micro deposits verification
          Alert.alert(
            'Micro Deposits Sent',
            'We\'ve sent two small deposits to your account. Please check your bank statement and enter the amounts below.',
            [{ text: 'OK', onPress: () => setCurrentStep(3) }]
          );
        }
      } else {
        Alert.alert('Error', 'Failed to create bank account. Please try again.');
      }
    } catch (error) {
      console.error('Error in verification:', error);
      Alert.alert('Error', 'An error occurred during verification.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicroDepositVerification = async () => {
    if (!verificationData.microDepositAmount1 || !verificationData.microDepositAmount2) {
      Alert.alert('Error', 'Please enter both micro deposit amounts');
      return;
    }

    setIsLoading(true);
    try {
      // Get the bank account ID (in real app, this would be stored)
      const accounts = await bankAccountService.getBankAccounts(merchantId);
      const bankAccount = accounts[0];
      
      if (bankAccount) {
        const isVerified = await bankAccountService.verifyBankAccount(bankAccount.id, {
          microDepositAmount1: parseFloat(verificationData.microDepositAmount1),
          microDepositAmount2: parseFloat(verificationData.microDepositAmount2)
        });

        if (isVerified) {
          Alert.alert(
            'Verification Successful!',
            'Your bank account has been verified and is ready to receive payouts.',
            [{ text: 'Continue', onPress: () => setCurrentStep(4) }]
          );
        } else {
          Alert.alert('Verification Failed', 'The amounts you entered do not match our records. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error verifying micro deposits:', error);
      Alert.alert('Error', 'An error occurred during verification.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    Alert.alert(
      'Setup Complete!',
      'Your bank account is now set up and ready to receive payouts from customer bookings.',
      [
        {
          text: 'OK',
          onPress: () => {
            if (onComplete) {
              // Get the created bank account
              bankAccountService.getBankAccounts(merchantId).then(accounts => {
                if (accounts.length > 0) {
                  onComplete(accounts[0]);
                }
              });
            }
            onBack();
          }
        }
      ]
    );
  };

  const renderStep1 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Account Information</Text>
      <Text style={styles.stepDescription}>
        Enter your bank account details to receive payouts from customer bookings.
      </Text>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Account Holder Name *</Text>
        <AppTextInput
          style={styles.input}
          value={formData.accountHolderName}
          onChangeText={(text) => handleInputChange('accountHolderName', text)}
          placeholder="Full name as it appears on bank account"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Account Type *</Text>
        <View style={styles.radioGroup}>
          <TouchableOpacity
            style={[styles.radioOption, formData.accountType === 'checking' && styles.radioOptionSelected]}
            onPress={() => handleInputChange('accountType', 'checking')}
          >
            <Text style={[styles.radioText, formData.accountType === 'checking' && styles.radioTextSelected]}>
              Checking
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.radioOption, formData.accountType === 'savings' && styles.radioOptionSelected]}
            onPress={() => handleInputChange('accountType', 'savings')}
          >
            <Text style={[styles.radioText, formData.accountType === 'savings' && styles.radioTextSelected]}>
              Savings
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Routing Number *</Text>
        <AppTextInput
          style={styles.input}
          value={formData.routingNumber}
          onChangeText={(text) => handleInputChange('routingNumber', text.replace(/\D/g, '').slice(0, 9))}
          placeholder="9-digit routing number"
          keyboardType="numeric"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Account Number *</Text>
        <AppTextInput
          style={styles.input}
          value={formData.accountNumber}
          onChangeText={(text) => handleInputChange('accountNumber', text.replace(/\D/g, ''))}
          placeholder="Account number"
          keyboardType="numeric"
          secureTextEntry
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Confirm Account Number *</Text>
        <AppTextInput
          style={styles.input}
          value={formData.confirmAccountNumber}
          onChangeText={(text) => handleInputChange('confirmAccountNumber', text.replace(/\D/g, ''))}
          placeholder="Re-enter account number"
          keyboardType="numeric"
          secureTextEntry
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Bank Name *</Text>
        <AppTextInput
          style={styles.input}
          value={formData.bankName}
          onChangeText={(text) => handleInputChange('bankName', text)}
          placeholder="e.g., Chase, Bank of America"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Bank Address</Text>
        <AppTextInput
          style={[styles.input, styles.textArea]}
          value={formData.bankAddress}
          onChangeText={(text) => handleInputChange('bankAddress', text)}
          placeholder="Bank address (optional)"
          multiline
          numberOfLines={3}
        />
      </View>
    </View>
  );

  const renderStep2 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Bank Verification</Text>
      <Text style={styles.stepDescription}>
        Choose how you'd like to verify your bank account.
      </Text>

      <View style={styles.verificationOptions}>
        <TouchableOpacity
          style={[styles.verificationOption, formData.verificationMethod === 'instant_verification' && styles.verificationOptionSelected]}
          onPress={() => handleInputChange('verificationMethod', 'instant_verification')}
        >
          <Text style={styles.verificationTitle}>⚡ Instant Verification</Text>
          <Text style={styles.verificationDescription}>
            Verify instantly using your online banking credentials
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.verificationOption, formData.verificationMethod === 'micro_deposits' && styles.verificationOptionSelected]}
          onPress={() => handleInputChange('verificationMethod', 'micro_deposits')}
        >
          <Text style={styles.verificationTitle}>💰 Micro Deposits</Text>
          <Text style={styles.verificationDescription}>
            We'll send two small deposits to verify your account (1-2 business days)
          </Text>
        </TouchableOpacity>
      </View>

      {formData.verificationMethod === 'micro_deposits' && currentStep === 3 && (
        <View style={styles.microDepositForm}>
          <Text style={styles.microDepositTitle}>Enter Micro Deposit Amounts</Text>
          <Text style={styles.microDepositDescription}>
            Check your bank statement for two small deposits from BookAPT and enter the amounts below.
          </Text>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>First Deposit Amount *</Text>
            <AppTextInput
              style={styles.input}
              value={verificationData.microDepositAmount1}
              onChangeText={(text) => setVerificationData(prev => ({ ...prev, microDepositAmount1: text }))}
              placeholder="0.00"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Second Deposit Amount *</Text>
            <AppTextInput
              style={styles.input}
              value={verificationData.microDepositAmount2}
              onChangeText={(text) => setVerificationData(prev => ({ ...prev, microDepositAmount2: text }))}
              placeholder="0.00"
              keyboardType="numeric"
            />
          </View>

          <TouchableOpacity
            style={[styles.button, styles.primaryButton]}
            onPress={handleMicroDepositVerification}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="white" />
            ) : (
              <Text style={styles.buttonText}>Verify Account</Text>
            )}
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  const renderStep3 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Payout Settings</Text>
      <Text style={styles.stepDescription}>
        Configure how often you'd like to receive payouts.
      </Text>

      <View style={styles.payoutOptions}>
        <TouchableOpacity style={styles.payoutOption}>
          <Text style={styles.payoutTitle}>📅 Weekly Payouts</Text>
          <Text style={styles.payoutDescription}>Every Friday</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.payoutOption}>
          <Text style={styles.payoutTitle}>📅 Daily Payouts</Text>
          <Text style={styles.payoutDescription}>Every business day</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.payoutOption}>
          <Text style={styles.payoutTitle}>📅 Monthly Payouts</Text>
          <Text style={styles.payoutDescription}>First business day of month</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.infoBox}>
        <Text style={styles.infoTitle}>💡 Payout Information</Text>
        <Text style={styles.infoText}>
          • Payouts are processed automatically based on your schedule{'\n'}
          • Minimum payout amount: $10{'\n'}
          • Processing time: 1-2 business days{'\n'}
          • You can change your payout schedule anytime in settings
        </Text>
      </View>
    </View>
  );

  const renderStep4 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Setup Complete! 🎉</Text>
      <Text style={styles.stepDescription}>
        Your bank account has been successfully set up and verified.
      </Text>

      <View style={styles.successCard}>
        <Text style={styles.successTitle}>✅ Account Verified</Text>
        <Text style={styles.successText}>
          {formData.bankName} ••••{formData.accountNumber.slice(-4)}
        </Text>
        <Text style={styles.successSubtext}>
          {formData.accountType.charAt(0).toUpperCase() + formData.accountType.slice(1)} Account
        </Text>
      </View>

      <View style={styles.nextSteps}>
        <Text style={styles.nextStepsTitle}>What's Next?</Text>
        <Text style={styles.nextStepsText}>
          • Start accepting customer bookings{'\n'}
          • Payments will be automatically deposited{'\n'}
          • Monitor your earnings in the dashboard{'\n'}
          • Manage payouts in settings
        </Text>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Bank Account Setup</Text>
        <View style={styles.headerSpacer} />
      </View>

      {/* Progress Indicator */}
      <View style={styles.progressContainer}>
        {steps.map((step, index) => (
          <View key={index} style={styles.stepIndicator}>
            <View style={[
              styles.stepCircle,
              currentStep > index + 1 && styles.stepCircleCompleted,
              currentStep === index + 1 && styles.stepCircleActive
            ]}>
              <Text style={[
                styles.stepNumber,
                currentStep > index + 1 && styles.stepNumberCompleted,
                currentStep === index + 1 && styles.stepNumberActive
              ]}>
                {currentStep > index + 1 ? '✓' : index + 1}
              </Text>
            </View>
            <Text style={[
              styles.stepName,
              currentStep === index + 1 && styles.stepNameActive
            ]}>
              {step}
            </Text>
          </View>
        ))}
      </View>

      {/* Content */}
      <ScrollView style={styles.content}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </ScrollView>

      {/* Footer */}
      <View style={styles.footer}>
        {currentStep < 4 ? (
          <TouchableOpacity
            style={[styles.button, styles.primaryButton]}
            onPress={handleNext}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="white" />
            ) : (
              <Text style={styles.buttonText}>
                {currentStep === 3 ? 'Complete Setup' : 'Next'}
              </Text>
            )}
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={[styles.button, styles.primaryButton]}
            onPress={handleComplete}
          >
            <Text style={styles.buttonText}>Finish Setup</Text>
          </TouchableOpacity>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  backButton: {
    marginRight: 15,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    flex: 1,
  },
  headerSpacer: {
    width: 60,
  },
  progressContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  stepIndicator: {
    flex: 1,
    alignItems: 'center',
  },
  stepCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#E1E5E9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  stepCircleActive: {
    backgroundColor: '#007AFF',
  },
  stepCircleCompleted: {
    backgroundColor: '#34C759',
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#8E8E93',
  },
  stepNumberActive: {
    color: 'white',
  },
  stepNumberCompleted: {
    color: 'white',
  },
  stepName: {
    fontSize: 12,
    color: '#8E8E93',
    textAlign: 'center',
  },
  stepNameActive: {
    color: '#007AFF',
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  stepContainer: {
    padding: 20,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  stepDescription: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 30,
    lineHeight: 24,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1C1C1E',
    backgroundColor: 'white',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  radioGroup: {
    flexDirection: 'row',
    gap: 12,
  },
  radioOption: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    backgroundColor: 'white',
    alignItems: 'center',
  },
  radioOptionSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#F0F8FF',
  },
  radioText: {
    fontSize: 16,
    color: '#1C1C1E',
  },
  radioTextSelected: {
    color: '#007AFF',
    fontWeight: '600',
  },
  verificationOptions: {
    gap: 16,
  },
  verificationOption: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    backgroundColor: 'white',
  },
  verificationOptionSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#F0F8FF',
  },
  verificationTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  verificationDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  microDepositForm: {
    marginTop: 20,
    padding: 20,
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  microDepositTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  microDepositDescription: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 20,
    lineHeight: 20,
  },
  payoutOptions: {
    gap: 12,
  },
  payoutOption: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    backgroundColor: 'white',
  },
  payoutTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  payoutDescription: {
    fontSize: 14,
    color: '#8E8E93',
  },
  infoBox: {
    marginTop: 20,
    padding: 16,
    backgroundColor: '#F0F8FF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#007AFF',
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#007AFF',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#007AFF',
    lineHeight: 20,
  },
  successCard: {
    padding: 20,
    backgroundColor: '#E8F5E8',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#34C759',
    marginBottom: 20,
  },
  successTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2E7D32',
    marginBottom: 8,
  },
  successText: {
    fontSize: 16,
    color: '#2E7D32',
    marginBottom: 4,
  },
  successSubtext: {
    fontSize: 14,
    color: '#4CAF50',
  },
  nextSteps: {
    padding: 16,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  nextStepsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  nextStepsText: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  footer: {
    padding: 20,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
  },
  button: {
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: '#007AFF',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default BankAccountSetupScreen;

